import React, { useMemo } from 'react';
import { Box, HStack } from '@televet/kibble-ui/build/chakra';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import styled from 'styled-components/macro';
import { useSidePanelSearch } from '../../state/providers/SearchPanelProvider';
import { PanelViews } from 'shared/components/SidePanel/shared/types/enums/PanelViews';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { usePets } from '../../components/Benefits/hooks/usePets';
import Tooltip from 'shared/components/Tooltip';
import { useCheckPetParentHasEnrolledPetSubscription } from 'shared/types/graphql';
import { CareMembershipEvents } from 'shared/providers/TrackingProvider/EventNames';
import { isEnrollmentConsideredActive } from '../../components/Benefits/utils/careEnrollments';
import { useTracking } from 'shared/providers/TrackingProvider';

const SidePanelHeader = (): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const hasCareEnabled = isFeatureEnabled(FeatureFlagName.CarePlans);
  const { trackEvent } = useTracking();
  const {
    currentView,
    currentClinicPetParentId,
    currentClinicPetId,
    viewClinicPetParent,
    viewClinicPet,
    viewBenefits,
  } = useSidePanelSearch();
  const { pets, isTestPetParent, refetchPets, organizationPetParentId } = usePets(
    currentClinicPetParentId || undefined,
    currentClinicPetId,
  );

  const isCareMember = useMemo(() => {
    const petParentEnrollments = pets.flatMap((pet) => pet.organizationPet?.carePlanEnrollments) || [];
    const enrollments = petParentEnrollments.filter((enrollment) => isEnrollmentConsideredActive(enrollment));
    return !!enrollments.length;
  }, [pets]);

  useCheckPetParentHasEnrolledPetSubscription({
    variables: {
      where: {
        organizationPetParentId: organizationPetParentId || '',
      },
    },
    skip: !organizationPetParentId,
    onData: ({ data }) => {
      if (data.data?.checkPetParentHasEnrolledPet.hasEnrolledPet) {
        refetchPets();
      }
    },
  });

  return (
    <>
      <HStack
        justify="space-between"
        align="center"
        py={3}
        borderBottomWidth="1px"
        borderBottomColor="border.default"
        px={hasCareEnabled ? '64px' : '100px'}
        zIndex="overlay"
      >
        <StyledIcon
          aria-disabled={!currentClinicPetId}
          isCurrentView={currentView === PanelViews.PetProfile}
          data-testid="pet-profile-button"
          onClick={(): void => {
            if (currentClinicPetId) {
              viewClinicPet({ clinicPetId: currentClinicPetId });
            }
          }}
        >
          <Tooltip disabled={!!currentClinicPetId} content="There are not pets associated with this client.">
            <Icon name="paw" size="xl" variant={currentView === PanelViews.PetProfile ? 'interactive' : 'default'} />
          </Tooltip>
        </StyledIcon>
        <StyledIcon
          isCurrentView={currentView === PanelViews.PetParentProfile}
          data-testid="pet-parent-profile-button"
          onClick={(): void => {
            if (currentClinicPetParentId) {
              viewClinicPetParent({ clinicPetParentId: currentClinicPetParentId });
            }
          }}
        >
          <Icon
            name="personCircle"
            size="xl"
            variant={
              currentView === PanelViews.PetParentProfile || currentView === PanelViews.LoyaltyRewards
                ? 'interactive'
                : 'default'
            }
          />
        </StyledIcon>
        {hasCareEnabled && (
          <StyledIcon
            isCurrentView={currentView === PanelViews.Benefits}
            data-testid="pet-parent-profile-button"
            onClick={(): void => {
              trackEvent(CareMembershipEvents.ViewBenefits);
              viewBenefits({ clinicPetParentId: currentClinicPetParentId || '' });
            }}
          >
            <Box display="inline-flex">
              <Icon
                variant={currentView === PanelViews.Benefits ? 'interactive' : isCareMember ? 'warning' : 'default'}
                name="starCircle"
                size="xl"
              />
            </Box>
          </StyledIcon>
        )}
      </HStack>
      {!!isTestPetParent && (
        <Box position="relative" top="-4px" zIndex="3" mt="0px !important">
          <Box position="absolute" width="100%" height={1} bg="background.warning" />
          <Box
            position="absolute"
            width="100%"
            height={1}
            backgroundImage={`url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2300000029' stroke-width='8' stroke-dasharray='10' stroke-dashoffset='100%25' stroke-linecap='butt'/%3e%3c/svg%3e")`}
          />
        </Box>
      )}
    </>
  );
};

interface IIconProps {
  isCurrentView: boolean;
  hasPlan?: boolean;
}

const StyledIcon = styled.div<IIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }

  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default SidePanelHeader;
